import { useEffect } from 'react';
import {
  Alert,
  Drawer,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  TextField,
} from '@elementary/common-ui/components';
import { Divider, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { METADATA_VALUES } from '@elementary/common-ui/constants/queryKeys';
import { useFormContext } from 'react-hook-form';
import { RuleFormFields } from 'shared/types';
import * as sharedApi from 'shared/services/api';
import { DestinationSection } from 'shared/components/AlertDestinations';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';
import CadenceFormContent from './CadenceFormContent';
import ImpactedAssetsFiltersFormContent from './ImpactedAssetsFiltersFormContent';
import IncidentFilterFormContent from './IncidentFilterFormContent';
import RuleCategoriesFormContent from './RuleCategoriesFormContent';
import useRuleDrawerFilterData from './useRuleDrawerFilterData';

type Props = {
  open: boolean;
  onClose: () => void;
  envId: string;
  title: string;
  namePlaceholder: string;
  onSubmit: () => void;
  isLoading: boolean;
  isEditMode: boolean;
  errorMessage?: string;
  showCadence: boolean;
};

const RuleDrawer = ({
  open,
  onClose,
  envId,
  title,
  namePlaceholder,
  onSubmit,
  isLoading,
  isEditMode,
  errorMessage,
  showCadence,
}: Props) => {
  const methods = useFormContext<RuleFormFields>();
  const { assets, paths } = useRuleDrawerFilterData(envId);

  const { data: metadataData } = useQuery([METADATA_VALUES, envId], () =>
    sharedApi.fetchMetadata(envId),
  );

  const isDisabled = !methods.watch('destinations')?.length;

  const isPipelineSelected = methods
    .watch(showCadence ? 'incidentCategories' : 'alertCategories')
    ?.includes('pipeline');

  const { setValue } = methods;
  useEffect(() => {
    if (isPipelineSelected) {
      setValue('incidentFilters', [], { shouldValidate: true });
      setValue('assetFilters', []);
      setValue('ruleTypeIncidents', 'all', { shouldValidate: true });
    }
  }, [isPipelineSelected, setValue]);

  return (
    <Drawer
      isOpen={open}
      title={`${isEditMode ? 'Edit' : 'Create'} ${title}`}
      onClose={onClose}
      submitButtonLabel={isLoading ? 'Saving' : isEditMode ? 'Save' : 'Create'}
      onSubmit={onSubmit}
      submitDisabled={!methods.formState.isValid || isDisabled}
      isLoading={isLoading}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <FormField
          title={`Name your ${title}`}
          errorMessage={
            methods.formState.errors.name?.message as string | undefined
          }
        >
          <TextField
            {...methods.register('name', {
              required: requiredLabel,
            })}
            variant="outlined"
            placeholder={namePlaceholder}
          />
        </FormField>

        <Divider sx={{ mx: '-24px', my: 3 }} />

        <DestinationSection
          destinations={methods.watch('destinations') ?? []}
          setDestinationsValue={(destinations) =>
            methods.setValue('destinations', destinations)
          }
        />

        <Divider sx={{ mx: '-24px', my: 3 }} />

        {showCadence && (
          <>
            <CadenceFormContent methods={methods} />
            <Divider sx={{ mx: '-24px', my: 3 }} />
          </>
        )}

        <RuleCategoriesFormContent
          fieldName={showCadence ? 'incidentCategories' : 'alertCategories'}
          title={showCadence ? 'Incident categories' : 'Alert categories'}
        />

        <Divider sx={{ mx: '-24px', my: 3 }} />

        <Stack gap={2}>
          {showCadence && (
            <>
              <Typography
                sx={{
                  fontWeight: fontWeight('medium'),
                  fontSize: fontSize('base'),
                }}
              >
                Incident filters
              </Typography>
              <Typography
                sx={{ fontSize: fontSize('sm'), color: 'content.secondary' }}
              >
                Optionally filter which incidents are included in the digest.
              </Typography>
            </>
          )}
          <IncidentFilterFormContent
            methods={methods}
            assets={assets}
            metadataData={metadataData}
            showRuleTypeToggle={!showCadence}
            disabled={isPipelineSelected}
          />
        </Stack>

        <Divider sx={{ mx: '-24px', my: 3 }} />

        <Stack gap={2}>
          <ImpactedAssetsFiltersFormContent
            methods={methods}
            assets={assets}
            paths={paths}
            metadataData={metadataData}
            descriptionText={
              showCadence
                ? 'Include incidents only if one or more of the selected assets are impacted.'
                : undefined
            }
            disabled={isPipelineSelected}
          />
        </Stack>

        {errorMessage && (
          <>
            <Divider sx={{ mx: '-24px', my: 3 }} />
            <Alert
              style={{ width: '100%', minWidth: 'unset' }}
              severity="error"
            >
              <Typography sx={{ fontSize: fontSize('base') }}>
                {errorMessage}
              </Typography>
            </Alert>
          </>
        )}
      </form>
    </Drawer>
  );
};

export default RuleDrawer;
